import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { api, formatApiErrorDetail } from '../utils/api';
import { Plus, Save, X, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import BACKEND_BASE from '../utils/backend';

const Controls = () => {
  const [items, setItems] = useState([]);
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', type: 'visual', icon_id: '' });

  const controlTypes = [
    { value: 'visual', label: 'Visuel' },
    { value: 'temperature', label: 'Temperature' },
    { value: 'checklist', label: 'Check-list' },
    { value: 'validation', label: 'Validation' },
  ];

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [itemsRes, iconsRes] = await Promise.all([api.get('/controls'), api.get('/icons')]);
      setItems(itemsRes.data);
      setIcons(iconsRes.data);
    } catch (error) {
      toast.error('Echec du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, icon_id: formData.icon_id || null };
      if (editingId) {
        await api.put(`/controls/${editingId}`, payload);
        toast.success('Controle modifie');
      } else {
        await api.post('/controls', payload);
        toast.success('Controle cree');
      }
      resetForm();
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleEdit = (item) => {
    setFormData({ name: item.name, type: item.type || 'visual', icon_id: item.icon_id || '' });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce controle ?')) return;
    try {
      await api.delete(`/controls/${id}`);
      toast.success('Controle supprime');
      loadData();
    } catch (error) {
      toast.error('Echec de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', type: 'visual', icon_id: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const getIconPreview = (iconId) => {
    const icon = icons.find((i) => i.id === iconId);
    if (!icon || !icon.image_url) return null;
    return <img src={BACKEND_BASE + icon.image_url} alt={icon.name} className="w-8 h-8 object-contain inline-block" />;
  };

  const getTypeBadge = (type) => {
    const colors = {
      visual: { bg: '#DBEAFE', text: '#1E40AF' },
      temperature: { bg: '#FEF3C7', text: '#92400E' },
      checklist: { bg: '#D1FAE5', text: '#065F46' },
      validation: { bg: '#EDE9FE', text: '#5B21B6' },
    };
    const c = colors[type] || { bg: '#F3F4F6', text: '#374151' };
    const label = controlTypes.find((t) => t.value === type)?.label || type;
    return <span className="px-3 py-1 rounded text-sm" style={{ backgroundColor: c.bg, color: c.text }}>{label}</span>;
  };

  if (loading) return <Layout><div className="text-center py-12">Chargement...</div></Layout>;

  return (
    <Layout>
      <div data-testid="controls-page">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold" style={{ color: '#0E2D43', fontFamily: 'Work Sans, sans-serif' }}>
            Controles
          </h1>
          {!showForm && (
            <button onClick={() => setShowForm(true)} data-testid="add-control-button"
              className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
              style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
              <Plus size={20} /><span>Ajouter</span>
            </button>
          )}
        </div>

        {showForm && (
          <div className="bg-white rounded-lg shadow p-6 border mb-8" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: '#0E2D43' }}>
              {editingId ? 'Modifier Controle' : 'Nouveau Controle'}
            </h2>
            <form onSubmit={handleSubmit} data-testid="control-form">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Nom *</label>
                  <input type="text" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="control-name-input"
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}
                    placeholder="ex: Visuel, Temperature, Check-list" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Type *</label>
                  <select value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    data-testid="control-type-select"
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}>
                    {controlTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Icone</label>
                  <select value={formData.icon_id}
                    onChange={(e) => setFormData({ ...formData, icon_id: e.target.value })}
                    data-testid="control-icon-select"
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}>
                    <option value="">-- Aucune icone --</option>
                    {icons.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex space-x-3">
                <button type="submit" data-testid="save-control-button"
                  className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
                  style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
                  <Save size={18} /><span>Enregistrer</span>
                </button>
                <button type="button" onClick={resetForm}
                  className="flex items-center space-x-2 px-4 py-2 rounded border"
                  style={{ borderColor: '#D1D5DB', color: '#0E2D43' }}>
                  <X size={18} /><span>Annuler</span>
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
          <table className="w-full">
            <thead style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium">Icone</th>
                <th className="px-6 py-3 text-left text-sm font-medium">Nom</th>
                <th className="px-6 py-3 text-left text-sm font-medium">Type</th>
                <th className="px-6 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="4" className="px-6 py-8 text-center" style={{ color: '#6B7280' }}>
                  Aucun controle defini. Ajoutez vos premiers controles !
                </td></tr>
              ) : items.map((item) => (
                <tr key={item.id} data-testid={`control-row-${item.id}`} className="border-t" style={{ borderColor: '#E5E7EB' }}>
                  <td className="px-6 py-4">{item.icon_id ? getIconPreview(item.icon_id) : <span style={{ color: '#9CA3AF' }}>-</span>}</td>
                  <td className="px-6 py-4 font-medium" style={{ color: '#0E2D43' }}>{item.name}</td>
                  <td className="px-6 py-4">{getTypeBadge(item.type)}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => handleEdit(item)} data-testid={`edit-control-${item.id}`}
                      className="inline-flex items-center px-3 py-1 rounded mr-2"
                      style={{ backgroundColor: '#F3F4F6', color: '#0E2D43' }}><Edit size={16} /></button>
                    <button onClick={() => handleDelete(item.id)} data-testid={`delete-control-${item.id}`}
                      className="inline-flex items-center px-3 py-1 rounded"
                      style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default Controls;
